import { apiCandidates } from "@/api/candidate.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { applicantsKeys } from "@/queries/recruit/use-applicants.query.ts";
import { candidateActionsKeys } from "@/queries/recruit/use-candidate-actions.query.ts";
import { jobsKeys } from "@/queries/recruit/use-jobs.query.ts";
import { ApiErrorException, ApiPassException } from "@/types/api.types.ts";
import { ApiCandidateCreatePayload } from "@/types/recruit/candidate.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";

export const candidatesKeys = createQueryKeys("candidates", {
  listByApplicantId: (id: number | undefined) => ({
    queryKey: [id],
    queryFn: async () => {
      if (id == undefined) throw new ApiPassException("No id");
      const result = await apiCandidates.getByApplicantId(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export const useCandidatesByApplicant = (id: number | undefined) => {
  return useQuery({
    ...candidatesKeys.listByApplicantId(id),
    staleTime: 5 * 1000,
  });
};

export const useCandidateCreate = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (payload: ApiCandidateCreatePayload) => {
      const result = await apiCandidates.create(payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: candidatesKeys.listByApplicantId._def,
      });
      queryClient.invalidateQueries({
        queryKey: applicantsKeys.search._def,
      });
      queryClient.invalidateQueries({
        queryKey: applicantsKeys.searchInfinite._def,
      });
      queryClient.invalidateQueries({
        queryKey: jobsKeys.search._def,
      });
      queryClient.invalidateQueries({
        queryKey: jobsKeys.details._def,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useCandidateDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiCandidates.delete(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: candidatesKeys.listByApplicantId._def,
      });
      queryClient.invalidateQueries({
        queryKey: applicantsKeys.search._def,
      });
      queryClient.invalidateQueries({
        queryKey: applicantsKeys.searchInfinite._def,
      });
      queryClient.invalidateQueries({
        queryKey: jobsKeys.search._def,
      });
      queryClient.invalidateQueries({
        queryKey: jobsKeys.details._def,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useCandidateSnooze = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiCandidates.snooze(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: candidatesKeys.listByApplicantId._def,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useCandidateUpdateAIScore = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ id, score }: { id: number; score: number }) => {
      const result = await apiCandidates.updateScore(id, score);
      if (result.error) throw new ApiErrorException(result.error);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: candidatesKeys.listByApplicantId._def,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useEmailSequenceRunCancel = (candidateId: number) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (runId: number) => {
      const result = await apiCandidates.cancelEmailSequenceRun(runId);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: candidateActionsKeys.list(candidateId).queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};
